import React, { useState, useEffect } from 'react';
import { useSetAtom, useAtomValue, useAtom } from 'jotai';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { DeviceEUIProps, eSensorChartAtom, EsensorChart, ParsedSensorInfo, sensorInfoAtom } from '../atoms-types/eSensors';
import { Card, CardContent, Typography, Box } from '@mui/material';
import moment, { Moment } from 'moment-timezone';
import DownloadButton from '../buttons/downloadButton';
import SensorGraph from '../charts-graphs/sensorGraph';
import GraphTimePicker from '../buttons/graphTimePicker';
import { endpointLink } from '../../index';

interface ExtendedDeviceEUIProps extends DeviceEUIProps {
  subClass?: 'temp' | 'wind' | null;
  sensorInfo?: ParsedSensorInfo;
  onRangeChange?: (range: string) => void;
}

const EsensorChartHandler: React.FC<ExtendedDeviceEUIProps> = ({ deviceEUI, Display, subClass, sensorInfo, onRangeChange }) => {
  const setEsensorChart = useSetAtom(eSensorChartAtom);
  const esensorChartData = useAtomValue(eSensorChartAtom);
  const [sensorInfoAtomValue] = useAtom(sensorInfoAtom);
  const [startDateTime, setStartDateTime] = useState<Moment>(moment().subtract(24, 'hour').tz('America/New_York'));
  const [endDateTime, setEndDateTime] = useState<Moment>(moment().tz('America/New_York'));
  const [selectedRange, setSelectedRange] = useState<string>("Last 24 hours");

  // Notify parent when range changes
  useEffect(() => {
    onRangeChange?.(selectedRange);
  }, [selectedRange, onRangeChange]);

  // Update chart data when sensorInfoAtom changes for the current sensor
  useEffect(() => {
    if (!deviceEUI || selectedRange === "Custom") return;
    
    const currentSensorInfo = sensorInfoAtomValue[deviceEUI];
    if (!currentSensorInfo || currentSensorInfo.length === 0) return;
    
    const sensor = currentSensorInfo[0];
    if (!sensor.jInfo) return;
    
    // Get timestamp from jInfo or use current time
    const timestamp = (sensor.jInfo as any).Timestamp || new Date().toISOString();
    
    // Filter displays based on subClass for weather sensors
    let filteredDisplays = Display;
    if (subClass) {
      if (subClass === 'temp') {
        filteredDisplays = Display.filter(d => ['TempF', 'Humidity'].includes(d));
      } else if (subClass === 'wind') {
        filteredDisplays = Display.filter(d => ['WindAvg', 'WindMax', 'WindDir', 'BaroPress'].includes(d));
      }
    }

    // Create an object with all matching display values
    const jInfoObj = sensor.jInfo as Record<string, any>;
    const displayValues = Array.isArray(filteredDisplays) 
      ? filteredDisplays.reduce((acc, key) => {
          // Use the same logic as the cards
          const value = getSensorValue(jInfoObj, sensor.Type, key);
          acc[key] = value;
          return acc;
        }, {} as Record<string, number>)
      : { [filteredDisplays]: getSensorValue(jInfoObj, sensor.Type, filteredDisplays) };

    setEsensorChart(prev => {
      // Check if data with this timestamp already exists
      const timestampExists = prev.some(data => data.timestamp === timestamp);
      if (timestampExists) {
        return prev; // Return previous state if timestamp exists
      }
      
      // Add new data if timestamp doesn't exist
      const chartData: EsensorChart = {
        timestamp,
        ...displayValues
      };
      return [...prev, chartData];
    });
  }, [sensorInfoAtomValue, deviceEUI, Display, subClass, selectedRange, setEsensorChart]);

  // Create filtered display array for the chart
  const getFilteredDisplays = () => {
    if (subClass) {
      if (subClass === 'temp') {
        return Display.filter(d => ['TempF', 'Humidity'].includes(d));
      } else if (subClass === 'wind') {
        return Display.filter(d => ['WindAvg', 'WindMax', 'WindDir', 'BaroPress'].includes(d));
      }
    }
    return Display;
  };

  // Use the same logic as the cards to get sensor values
  const getSensorValue = (sensorData: any, sensorType: string, display: string): number => {
    // Type mapping for sensor types (same as in environmentalGrid)
    const sensorTypeMap: Record<string, any> = {
      "Weather": {},
      "RainGauge": {},
      "RiverHeight": {},
      "Tank": {},
      "Kiln": {},
      "SoilMt": {}
    };
    
    const type = sensorTypeMap[sensorType];
    if (!type) return 0;
    
    const value = (sensorData as typeof type)[display as keyof typeof type];
    return typeof value === 'number' ? value : 0;
  };

  const { refetch } = useQuery({
    queryKey: ['EsensorChart', deviceEUI, startDateTime, endDateTime],
    staleTime: 1000 * 60 * 5,
    queryFn: async () => {
      if (!deviceEUI) return [];
      const response = await axios.get(
        `${endpointLink}esensorchart/${deviceEUI}`, {
          params: {
            start: startDateTime.toISOString(),
            end: endDateTime.toISOString()
          }
        }
      );
      const data = response.data;
      console.log("EsensorChart data", data);
      UpdateSensorGraph(data);
      return data;
    },
    enabled: !!deviceEUI && !!Display,
  });
    
  useEffect(() => {
    refetch();
  }, [deviceEUI, startDateTime, endDateTime, refetch]);

  useEffect(() => {
    if (deviceEUI) {
      refetch();
    }
  }, [subClass, deviceEUI, refetch]);

  function UpdateSensorGraph(data: { Timestamp: string; jInfo: string }[]) {
    const updatedEsensorChart: EsensorChart[] = data?.map((entry) => {
      const parsedJInfo = JSON.parse(entry.jInfo || '{}');
      
      // Filter displays based on subClass for weather sensors
      let filteredDisplays = Display;
      if (subClass) {
        if (subClass === 'temp') {
          filteredDisplays = Display.filter(d => ['TempF', 'Humidity'].includes(d));
        } else if (subClass === 'wind') {
          filteredDisplays = Display.filter(d => ['WindAvg', 'WindMax', 'WindDir', 'BaroPress'].includes(d));
        }
      }

      // Create an object with all matching display values using the same logic as cards
      const displayValues = Array.isArray(filteredDisplays) 
        ? filteredDisplays.reduce((acc, key) => {
            // Use the same logic as the cards
            const value = getSensorValue(parsedJInfo, sensorInfo?.Type || 'Weather', key);
            acc[key] = value;
            return acc;
          }, {} as Record<string, number>)
        : { [filteredDisplays]: getSensorValue(parsedJInfo, sensorInfo?.Type || 'Weather', filteredDisplays) };

      return {
        timestamp: entry.Timestamp,
        ...displayValues
      };
    }) || [];
    
    setEsensorChart(prev => [...updatedEsensorChart]);
  }

  // const renderLoadingOrError = () => {
  //   if (isFetching) {
  //     return <p>Loading...</p>;
  //   } else if (isError) {
  //     return <p>Error: {error.message}</p>;
  //   }
  //   return null;
  // };

  return (
    <Card sx={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100%', justifyContent: 'center' }}>
      <CardContent>
        {deviceEUI ? (
          <>
            <SensorGraph 
              deviceEUI={deviceEUI} 
              Display={getFilteredDisplays()}
              startDateTime={startDateTime}
              endDateTime={endDateTime}
            />
            <CardContent style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '16px' }}>
              <GraphTimePicker
                startDateTime={startDateTime}
                endDateTime={endDateTime}
                setStartDateTime={setStartDateTime}
                setEndDateTime={setEndDateTime}
                selectedRange={selectedRange}
                setSelectedRange={setSelectedRange}
              />
              <DownloadButton
                data={esensorChartData.map((item: EsensorChart) => ({
                  Timestamp: item.timestamp,
                  ...(Array.isArray(Display) ? Display.reduce((acc, key) => ({
                    ...acc,
                    [key]: item[key]
                  }), {}) : { [Display]: item[Display] })
                }))}
                filename={`${deviceEUI}_${Array.isArray(Display) ? Display.join('_') : Display}_${startDateTime.format('YYYY-MM-DD')}_to_${endDateTime.format('YYYY-MM-DD')}`}
                timestampField="Timestamp"
                valueField={Array.isArray(Display) ? Display.join(',') : Display}
                disabled={!esensorChartData.length}
                timezone="America/New_York"
              />
            </CardContent>
          </>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <Typography variant="h6" color="text.secondary">
              Click on a sensor card to view its data
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

export default EsensorChartHandler;
